/**
 * Banebou (02 §5 type 2): hops all the time, about 62 px and 37 I-ticks a hop, reversing at walls;
 * with its player level with it, a 30/256 chance per hop to turn toward it; the walkers' vertical
 * jump when a platform is above. Ported from D_1F2E (state 4) and D_1FEF..D_214A.
 */

import { playerX, watchPlayer } from './chase';
import { hit, hitRow, OFF, platformAbove, probe, type Probe } from './map';
import { animate, fallStep, toggleFrame } from './motion';
import { CROUCH_START, DESCENT_STEPS, GAP_START, GAP_TABLE, JUMP_TABLE, LINE_Y, TOP_Y, WALK_STEP } from './tuning';
import { rngOf } from '../random';
import type { Monster, PlayerId, World } from '../types';

const TURN_CHANCE = 0x1e;
const drifting = (m: Monster): boolean => m.ai.driftL !== 0 || m.ai.driftR !== 0;

/** D_20D7: 2 px across; a wall beside the body (checked on the column grid, below Y $2D) reverses it. */
function across(world: World, m: Monster, p: Probe): void {
  const a = m.ai;
  const left = a.driftL < 0;
  if (!left && a.driftR >= 0) return;
  const base = left ? 0x00 : 0x03;
  const wall = m.y >= LINE_Y && p.subX === 0
    && (hit(world, p, base) || hit(world, p, base + 0x28) || (p.subY !== 0 && hit(world, p, base + 0x50)));
  if (!wall) {
    m.x = (m.x + (left ? -WALK_STEP : WALK_STEP)) & 0xff;
    return;
  }
  a.driftL = left ? 0 : -1;
  a.driftR = left ? -1 : 0;
  a.frame ^= 4;
}

/** D_20B0: the landing squash - frame to 2, then 3, then the hop is over. */
function settle(m: Monster): void {
  const a = m.ai;
  const f = a.frame & 3;
  if (f === 3) {
    a.jump = -1;
    a.descent = -1;
    toggleFrame(m);
  } else if (f === 2) a.frame++;
  else a.frame = (a.frame & 4) | 2;
}

/** D_1FEF: one tick of a hop or a vertical jump, by the counter. */
function hopTick(world: World, m: Monster, p: Probe): void {
  const a = m.ai;
  const n = a.jump;
  if (n >= 0x10) {
    a.jump = n - 1;
    if ((n & 3) === 0) a.frame = a.frame >= a.faceFrames ? 3 : 7;
    return;
  }
  if (n > 0) {
    // Winding up: frames 3 and 2 come down to 1 before the rise goes on.
    if ((a.frame & 3) >= 2) {
      a.frame--;
      return;
    }
    a.jump = n - 1;
    if (drifting(m)) {
      m.y = (m.y - 1) & 0xff;
      across(world, m, p);
    } else m.y = (m.y - (JUMP_TABLE[n] ?? 0)) & 0xff;
    toggleFrame(m);
    return;
  }
  if (a.descent < 0) a.descent = 0;
  if (a.descent === DESCENT_STEPS || (a.frame & 3) >= 2) {
    settle(m);
    return;
  }
  const step = a.descent++;
  m.y = (m.y + ((drifting(m) ? GAP_TABLE[step] : JUMP_TABLE[step]) ?? 0)) & 0xff;
  const crossed = m.y >= TOP_Y && ((m.y - 0x15) & 7) < p.subY;
  if (crossed && !hitRow(world, p, OFF.floor) && hitRow(world, p, OFF.below)) {
    m.y = (((m.y - LINE_Y) & 0xf8) + LINE_Y) & 0xff;
    settle(m);
    return;
  }
  across(world, m, p);
  toggleFrame(m);
}

/** D_1F2E: one AI update of a Banebou hunting player `target`. */
export function banebouUpdate(world: World, m: Monster, target: PlayerId): void {
  const a = m.ai;
  const p = probe(m);
  watchPlayer(world, m, target);
  if (a.jump >= 0 && !a.falling) {
    hopTick(world, m, p);
    return;
  }
  if (a.falling || m.y < TOP_Y || !hitRow(world, p, OFF.floor)) {
    // L_1F5B / D_EBB8: falling; only the landing animates (its JMP at $EB94 goes to L_EB0F).
    a.falling = true;
    if (fallStep(world, m, p)) {
      a.falling = false;
      animate(m);
    }
    return;
  }
  a.driftL = 0;
  a.driftR = 0;
  if (a.vertOk && platformAbove(world, p)) {
    a.jump = CROUCH_START;
    hopTick(world, m, p);
    return;
  }
  if (a.gapOk && rngOf(world).int(256) < TURN_CHANCE) a.frame = playerX(world, target) >= m.x ? 3 : 7;
  if (a.frame < a.faceFrames) a.driftR = -1;
  else a.driftL = -1;
  a.jump = GAP_START;
  hopTick(world, m, p);
}
